/**
 * Create / edit dialog for a WAF custom page. In edit mode name, type, and the
 * source `url` are pre-filled from the list row; `token` is a fixed per-type
 * placeholder (read-only).
 */
import { useEffect, useState } from 'react';
import { Copy, Eye } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  IconButton,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useCreateWafCustomPage,
  useUpdateWafCustomPage,
  wafExtractError,
  type WafCustomPage,
  type WafCustomPageInput,
} from '@/api/waf';
import { trackFeatureAction } from '@/lib/analytics-events';
import { CUSTOM_PAGE_TYPES, TOKEN_TYPES, TOKEN_VALUES } from './custom-page-types';
import { CustomPagePreviewDialog } from './CustomPagePreviewDialog';

/** A stacked label + control form cell. */
function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{label}</span>
      {children}
      {hint ? (
        <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-sm)' }}>{hint}</span>
      ) : null}
    </div>
  );
}

export function CustomPageFormDialog({
  serviceId,
  page,
  open,
  onOpenChange,
}: {
  serviceId: string;
  page?: WafCustomPage;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { show } = useToast();
  const isEdit = !!page;

  const [type, setType] = useState(page?.type ?? CUSTOM_PAGE_TYPES[0].value);
  const [name, setName] = useState(page?.name ?? '');
  const [url, setUrl] = useState(page?.url ?? '');
  const [previewOpen, setPreviewOpen] = useState(false);

  // Reset the form whenever the dialog (re)opens for a given page.
  useEffect(() => {
    if (open) {
      setType(page?.type ?? CUSTOM_PAGE_TYPES[0].value);
      setName(page?.name ?? '');
      setUrl(page?.url ?? '');
    }
  }, [open, page]);

  const needsToken = TOKEN_TYPES.has(type);
  // The token is a fixed placeholder per type — read-only, not user-editable.
  const token = TOKEN_VALUES[type] ?? '';

  const copyToken = () => {
    void navigator.clipboard.writeText(token);
    show({ tone: 'success', title: 'Copied' });
  };

  const create = useCreateWafCustomPage(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Custom page created' }); trackFeatureAction('waf', 'custom_page', 'create'); onOpenChange(false); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Create failed') }),
  });
  const update = useUpdateWafCustomPage(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Custom page updated' }); trackFeatureAction('waf', 'custom_page', 'edit'); onOpenChange(false); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Update failed') }),
  });

  const pending = create.isPending || update.isPending;
  const valid = name.trim() && url.trim() && (!needsToken || token.trim());
  // Editing without touching the URL → we can show the stored, platform-rendered content.
  const urlUnchanged = isEdit && url.trim() === (page?.url ?? '').trim() && !!page?.content;
  // Preview is available with stored content, or with an absolute URL (live iframe).
  const canPreview = urlUnchanged || (!!valid && /^https?:\/\//i.test(url.trim()));

  const submit = () => {
    if (!valid) return;
    const input: WafCustomPageInput = {
      type,
      name: name.trim(),
      url: url.trim(),
      ...(needsToken ? { token: token.trim() } : {}),
    };
    if (isEdit && page) update.mutate({ id: page.id, input });
    else create.mutate(input);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit custom page — ${page?.name}` : 'New custom page'}</DialogTitle>
          <DialogDescription>
            The platform fetches the source URL and renders it as the page served on the selected event.
          </DialogDescription>
        </DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          <Field label="Type">
            <Select value={type} onValueChange={setType} disabled={pending}>
              <SelectTrigger aria-label="Type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CUSTOM_PAGE_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Name">
            <Input
              placeholder="My block page"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={pending}
            />
          </Field>

          <Field label="Source URL">
            <Input
              placeholder="https://example.com/pages/block.html"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={pending}
            />
          </Field>

          {needsToken && (
            <Field label="Token" hint="Fixed placeholder for JS challenge / captcha — insert it into your page where the challenge box should render.">
              <Input
                value={token}
                readOnly
                endSlot={
                  <IconButton variant="ghost" size="sm" aria-label="Copy token" onClick={copyToken}>
                    <Copy size={13} />
                  </IconButton>
                }
              />
            </Field>
          )}

        </DialogBody>
        <DialogFooter>
          <Button
            variant="secondary"
            startIcon={<Eye size={15} />}
            onClick={() => setPreviewOpen(true)}
            disabled={!canPreview}
          >
            Preview
          </Button>
          <div style={{ flex: 1 }} />
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={pending || !valid}>
            {isEdit ? 'Save changes' : 'Create page'}
          </Button>
        </DialogFooter>
      </DialogContent>

      {previewOpen && (
        <CustomPagePreviewDialog
          name={name.trim() || 'Custom page'}
          // When editing an unchanged URL, preview the platform-rendered content
          // (no external request → can't be blocked). Otherwise fall back to a
          // live iframe of the URL, which the source site may refuse to embed.
          content={urlUnchanged ? page?.content : undefined}
          url={urlUnchanged ? undefined : url.trim()}
          open={previewOpen}
          onOpenChange={setPreviewOpen}
        />
      )}
    </Dialog>
  );
}

export default CustomPageFormDialog;
